import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getMenus,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getItems,
  createItem,
  updateItem,
  deleteItem,
  toggleAvailability,
  uploadImage,
  searchIngredients,
  aiTranslate,
  aiGenerateImage,
  getAiBgStyles,
  getAiCredits,
  aiReimageImage,
  bulkUpdatePrices,
} from "@/api/endpoints";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/confirm-dialog";
import {
  Plus,
  Trash2,
  Edit2,
  Upload,
  ChevronDown,
  ChevronRight,
  Package,
  Flame,
  Search,
  AlertTriangle,
  X,
  FileDown,
  Sparkles,
  Percent,
  QrCode,
} from "lucide-react";
import { ImageCropDialog } from "@/components/image-crop-dialog";
import { UpgradeDialog, isPlanError } from "@/components/upgrade-dialog";
import type { Category, Item, Ingredient } from "@/types";
import { PdfExportDialog } from "@/components/pdf-export-dialog";
import { MenuQrDialog } from "@/components/menu-qr-dialog";
import { MenuRenameDialog } from "@/components/menu-rename-dialog";

const BADGE_OPTIONS = [
  { id: "vegan", labelKey: "tag_vegan" },
  { id: "vegetarian", labelKey: "tag_vegetarian" },
  { id: "gluten-free", labelKey: "tag_gluten_free" },
  { id: "spicy", labelKey: "tag_spicy" },
  { id: "new", labelKey: "tag_new" },
  { id: "popular", labelKey: "tag_popular" },
  { id: "halal", labelKey: "tag_halal" },
  { id: "organic", labelKey: "tag_organic" },
  { id: "sugar-free", labelKey: "tag_sugar_free" },
  { id: "lactose-free", labelKey: "tag_lactose_free" },
];

const BADGE_LABEL_KEYS: Record<string, string> = Object.fromEntries(BADGE_OPTIONS.map((b) => [b.id, b.labelKey]));

const ALLERGEN_LABEL_KEYS: Record<string, string> = {
  gluten: "allergen_gluten",
  dairy: "allergen_dairy",
  egg: "allergen_egg",
  fish: "allergen_fish",
  shellfish: "allergen_shellfish",
  nuts: "allergen_nuts",
  soy: "allergen_soy",
  sesame: "allergen_sesame",
};

// === IngredientPicker ===
interface IngredientEntry {
  ingredientId: string;
  ingredient: Ingredient;
  grams: number;
}

function IngredientPicker({
  entries,
  onChange,
}: {
  entries: IngredientEntry[];
  onChange: (entries: IngredientEntry[]) => void;
}) {
  const { t, lang } = useI18n();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Ingredient[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (value: string) => {
    setQuery(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 1) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      const data = await searchIngredients(value, lang);
      setResults(data.filter((r) => !entries.some((e) => e.ingredientId === r.id)));
      setShowDropdown(true);
    }, 200);
  };

  const addIngredient = (ing: Ingredient) => {
    onChange([...entries, { ingredientId: ing.id, ingredient: ing, grams: 100 }]);
    setQuery("");
    setShowDropdown(false);
    setResults([]);
  };

  const updateGrams = (index: number, grams: number) => {
    const updated = [...entries];
    updated[index] = { ...updated[index], grams };
    onChange(updated);
  };

  const removeEntry = (index: number) => {
    onChange(entries.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {/* Search */}
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("search_ingredient")}
            value={query}
            onChange={(e) => handleSearch(e.target.value)}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            className="pl-10"
          />
        </div>
        {showDropdown && results.length > 0 && (
          <div className="absolute left-0 right-0 z-[9999] mt-1 max-h-60 overflow-y-auto rounded-lg border bg-card shadow-xl" style={{ bottom: "auto" }}>
            {results.map((ing) => (
              <button
                key={ing.id}
                type="button"
                className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-muted"
                onClick={() => addIngredient(ing)}
              >
                <span>{ing.name}</span>
                <span className="text-xs text-muted-foreground">
                  {ing.caloriesPer100g} kcal/100g
                  {ing.allergens.length > 0 && (
                    <span className="ml-1 text-amber-500">
                      ({ing.allergens.join(", ")})
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Added ingredients */}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry, i) => (
            <div key={entry.ingredientId} className="flex items-center gap-2 rounded-lg border bg-muted/30 px-3 py-2">
              <span className="flex-1 text-sm font-medium">{entry.ingredient.name}</span>
              <Input
                type="number"
                min={1}
                value={entry.grams}
                onChange={(e) => updateGrams(i, Number(e.target.value))}
                className="w-20 h-8 text-sm"
              />
              <span className="text-xs text-muted-foreground">g</span>
              <span className="w-16 text-right text-xs text-muted-foreground">
                {Math.round((entry.ingredient.caloriesPer100g * entry.grams) / 100)} kcal
              </span>
              <button type="button" onClick={() => removeEntry(i)} className="ml-1 text-muted-foreground hover:text-destructive">
                <X className="size-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// === NutritionSummary ===
function NutritionSummary({ entries }: { entries: IngredientEntry[] }) {
  const { t } = useI18n();
  if (entries.length === 0) return null;

  let cal = 0, pro = 0, fat = 0, carb = 0, fib = 0, weight = 0;
  const allergens = new Set<string>();

  for (const e of entries) {
    const r = e.grams / 100;
    cal += e.ingredient.caloriesPer100g * r;
    pro += e.ingredient.proteinPer100g * r;
    fat += e.ingredient.fatPer100g * r;
    carb += e.ingredient.carbsPer100g * r;
    fib += e.ingredient.fiberPer100g * r;
    weight += e.grams;
    for (const a of e.ingredient.allergens) allergens.add(a);
  }

  return (
    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
      <div className="flex items-center gap-2 text-sm font-semibold">
        <Flame className="size-4 text-orange-500" />
        <span>{Math.round(cal)} kcal</span>
        <span className="text-muted-foreground font-normal">/ {Math.round(weight)}g</span>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="text-center rounded bg-blue-50 p-1.5">
          <div className="font-bold text-blue-700">{Math.round(pro * 10) / 10}g</div>
          <div className="text-blue-500">Protein</div>
        </div>
        <div className="text-center rounded bg-amber-50 p-1.5">
          <div className="font-bold text-amber-700">{Math.round(fat * 10) / 10}g</div>
          <div className="text-amber-500">Yag</div>
        </div>
        <div className="text-center rounded bg-green-50 p-1.5">
          <div className="font-bold text-green-700">{Math.round(carb * 10) / 10}g</div>
          <div className="text-green-500">Karb</div>
        </div>
        <div className="text-center rounded bg-purple-50 p-1.5">
          <div className="font-bold text-purple-700">{Math.round(fib * 10) / 10}g</div>
          <div className="text-purple-500">Lif</div>
        </div>
      </div>
      {allergens.size > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <AlertTriangle className="size-3.5 text-amber-500" />
          {[...allergens].map((a) => (
            <Badge key={a} variant="secondary" className="text-xs bg-amber-50 text-amber-700">
              {t((ALLERGEN_LABEL_KEYS[a] || a) as Parameters<typeof t>[0])}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// === ItemRow ===
function ItemRow({
  item,
  currency,
  onToggle,
  onEdit,
  onDelete,
}: {
  item: Item;
  currency: string;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  return (
    <div className="rounded-lg border bg-background p-3">
      <div className="flex items-start gap-3">
        {item.image ? (
          <img src={item.image} alt="" className="size-12 shrink-0 rounded-lg object-cover" />
        ) : (
          <div className="flex size-12 shrink-0 items-center justify-center rounded-lg bg-muted">
            <Package className="size-5 text-muted-foreground" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`font-medium ${!item.isAvailable ? "line-through text-muted-foreground" : ""}`}>
              {item.name}
            </span>
            {item.badges.map((b) => (
              <Badge key={b} variant="secondary" className="text-xs">
                {t((BADGE_LABEL_KEYS[b] || b) as Parameters<typeof t>[0])}
              </Badge>
            ))}
            {item.totalCalories != null && (
              <Badge variant="outline" className="text-xs gap-1">
                <Flame className="size-3 text-orange-500" />
                {Math.round(item.totalCalories)} kcal
              </Badge>
            )}
            {item.calculatedAllergens && item.calculatedAllergens.length > 0 && (
              <Badge variant="outline" className="text-xs gap-1 text-amber-600 border-amber-200">
                <AlertTriangle className="size-3" />
                {item.calculatedAllergens.length} {t("allergen_count" as Parameters<typeof t>[0])}
              </Badge>
            )}
          </div>
          {item.description && (
            <p className="text-sm text-muted-foreground line-clamp-1">{item.description}</p>
          )}
          {item.variants.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-2">
              {item.variants.map((v) => (
                <span key={v.id} className="text-xs text-muted-foreground">
                  {v.name}: {v.price} {currency}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between border-t pt-2">
        <span className="font-semibold text-primary">
          {Number(item.price).toFixed(2)} {currency}
        </span>
        <div className="flex items-center gap-2">
          <Switch checked={item.isAvailable} onCheckedChange={onToggle} />
          <Button variant="ghost" size="icon" className="size-8" onClick={onEdit}>
            <Edit2 className="size-4" />
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" className="size-8">
                <Trash2 className="size-4 text-destructive" />
              </Button>
            }
            title={t("delete_item")}
            description={`"${item.name}" kalici olarak silinecek.`}
            confirmLabel={t("delete")}
            onConfirm={onDelete}
          />
        </div>
      </div>
    </div>
  );
}

// === CategoryFormDialog ===
function CategoryFormDialog({
  open,
  onClose,
  category,
  menuId,
}: {
  open: boolean;
  onClose: () => void;
  category?: Category;
  menuId: string;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [cropSrc, setCropSrc] = useState("");
  const [showCrop, setShowCrop] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [nameTranslations, setNameTranslations] = useState<Record<string, string>>({});
  const [transLangs, setTransLangs] = useState<string[]>([]);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const ALL_LANGS = [
    { code: "tr", flag: "🇹🇷" }, { code: "en", flag: "🇬🇧" }, { code: "de", flag: "🇩🇪" },
    { code: "fr", flag: "🇫🇷" }, { code: "es", flag: "🇪🇸" }, { code: "ar", flag: "🇸🇦" },
    { code: "ru", flag: "🇷🇺" }, { code: "zh", flag: "🇨🇳" }, { code: "ja", flag: "🇯🇵" },
    { code: "ko", flag: "🇰🇷" }, { code: "hi", flag: "🇮🇳" }, { code: "th", flag: "🇹🇭" },
  ];

  useEffect(() => {
    if (open) {
      setName(category?.name || "");
      setImageUrl(category?.image || "");
      const existing = category?.nameTranslations ? Object.keys(category.nameTranslations) : [];
      setNameTranslations(category?.nameTranslations || {});
      setTransLangs(existing);
      setCropSrc("");
      setShowCrop(false);
    }
  }, [open, category]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const nt = Object.keys(nameTranslations).length ? nameTranslations : undefined;
      if (category) {
        await updateCategory(category.id, { name, image: imageUrl || undefined, nameTranslations: nt } as Parameters<typeof updateCategory>[1]);
      } else {
        await createCategory(menuId, { name, image: imageUrl || undefined, nameTranslations: nt } as Parameters<typeof createCategory>[1]);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["categories", menuId] });
      onClose();
    },
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result as string); setShowCrop(true); };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const handleCropped = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadImage(file, "category", category?.id);
      setImageUrl(url);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="pb-4">
          <DialogTitle>{category ? t("edit_category" as Parameters<typeof t>[0]) : t("new_category")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); saveMutation.mutate(); }} className="space-y-4">
          <div>
            <Label>{t("category_name")} <span className="text-destructive">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label>{t("category_image")}</Label>
            <div className="mt-1 flex items-center gap-3">
              {imageUrl ? (
                <img src={imageUrl} alt="" className="h-16 w-28 rounded-lg object-cover" />
              ) : (
                <div className="flex h-16 w-28 items-center justify-center rounded-lg bg-muted text-xs text-muted-foreground">
                  {t("no_image")}
                </div>
              )}
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                <Upload className="mr-2 size-4" />
                {uploading ? t("uploading") : t("upload")}
              </Button>
            </div>
          </div>
          <ImageCropDialog
            open={showCrop}
            onClose={() => { setShowCrop(false); setCropSrc(""); }}
            imageSrc={cropSrc}
            aspectRatio={2}
            onCropped={handleCropped}
          />
          {/* Translations */}
          {transLangs.length > 0 && (
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {transLangs.map((code) => {
                    const lang = ALL_LANGS.find(l => l.code === code);
                    return (
                      <tr key={code} className="border-b last:border-0">
                        <td className="px-3 py-1.5 text-center text-lg w-10">{lang?.flag || "🌐"}</td>
                        <td className="px-2 py-1.5">
                          <Input className="h-8 text-sm" placeholder={name} value={nameTranslations[code] || ""} onChange={(e) => setNameTranslations({ ...nameTranslations, [code]: e.target.value })} />
                        </td>
                        <td className="px-2 py-1.5 w-8">
                          <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => {
                            setTransLangs(transLangs.filter(l => l !== code));
                            const nt = { ...nameTranslations }; delete nt[code]; setNameTranslations(nt);
                          }}><X className="size-4" /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div>
            <Button type="button" variant="outline" size="sm" onClick={() => setLangPickerOpen(true)}>
              <Plus className="mr-1 size-4" /> {t("add")} {t("language")}
            </Button>
            {langPickerOpen && (
              <>
                <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setLangPickerOpen(false)} />
                <div className="fixed left-1/2 top-1/2 z-[101] w-72 -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card shadow-2xl">
                  <div className="border-b px-4 py-3 font-semibold text-sm">{t("add")} {t("language")}</div>
                  <div className="max-h-48 overflow-y-auto">
                    {ALL_LANGS.filter(l => !transLangs.includes(l.code)).map(l => (
                      <button key={l.code} type="button" className="flex w-full items-center gap-3 px-4 py-2.5 text-sm hover:bg-muted" onClick={() => { setTransLangs([...transLangs, l.code]); setLangPickerOpen(false); }}>
                        <span className="text-lg">{l.flag}</span>
                        <span>{l.code.toUpperCase()}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
          <Button type="submit" className="w-full" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t("saving") : category ? t("save") : t("add")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// === CategorySection ===
function CategorySection({
  category,
  venueId,
  menuId,
  currency,
}: {
  category: Category;
  venueId: string;
  menuId: string;
  currency: string;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [editingCat, setEditingCat] = useState(false);
  const qc = useQueryClient();

  const { data: items } = useQuery({
    queryKey: ["items", category.id],
    queryFn: () => getItems(category.id),
  });

  const toggleMutation = useMutation({
    mutationFn: toggleAvailability,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items", category.id] }),
  });

  const deleteItemMutation = useMutation({
    mutationFn: deleteItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items", category.id] }),
  });

  const deleteCatMutation = useMutation({
    mutationFn: () => deleteCategory(category.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["categories", menuId] }),
  });

  return (
    <div className="rounded-xl border bg-card">
      <div className="flex items-center justify-between p-4">
        <button
          className="flex items-center gap-2 text-left"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {category.image ? (
            <img src={category.image} alt="" className="size-8 rounded object-cover" />
          ) : null}
          <span className="font-semibold">{category.name}</span>
          <Badge variant="secondary" className="text-xs">
            {items?.length ?? 0}
          </Badge>
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            onClick={() => setEditingCat(true)}
            title={t("edit")}
          >
            <Edit2 className="size-4" />
          </Button>
          <Button variant="ghost" size="icon" className="size-8 sm:size-auto sm:px-3 sm:py-1.5" onClick={() => setAddingItem(true)}>
            <Plus className="size-4 sm:mr-1" />
            <span className="hidden sm:inline text-sm">{t("add_item")}</span>
          </Button>
          <ConfirmDialog
            trigger={
              <Button variant="ghost" size="icon" className="size-8">
                <Trash2 className="size-4 text-destructive" />
              </Button>
            }
            title={t("delete_category")}
            description={`"${category.name}" ${t("delete_category_desc")}`}
            confirmLabel={t("delete")}
            onConfirm={() => deleteCatMutation.mutate()}
          />
        </div>
      </div>

      <CategoryFormDialog
        open={editingCat}
        onClose={() => setEditingCat(false)}
        category={category}
        menuId={menuId}
      />

      {expanded && (
        <div className="space-y-2 px-4 pb-4">
          {items?.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">
              {t("no_items")}
            </p>
          ) : (
            items?.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                currency={currency}
                onToggle={() => toggleMutation.mutate(item.id)}
                onEdit={() => setEditingItem(item)}
                onDelete={() => deleteItemMutation.mutate(item.id)}
              />
            ))
          )}
        </div>
      )}

      <ItemFormDialog
        open={addingItem || !!editingItem}
        onClose={() => {
          setAddingItem(false);
          setEditingItem(null);
        }}
        item={editingItem}
        categoryId={category.id}
        currency={currency}
        venueId={venueId}
      />
    </div>
  );
}

// === ItemFormDialog ===
function ItemFormDialog({
  open,
  onClose,
  item,
  categoryId,
  currency,
  venueId,
}: {
  open: boolean;
  onClose: () => void;
  item: Item | null;
  venueId: string;
  categoryId: string;
  currency: string;
}) {
  const qc = useQueryClient();
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [oldPrice, setOldPrice] = useState("");
  const [image, setImage] = useState("");
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [loyaltyPoints, setLoyaltyPoints] = useState("");
  const [stock, setStock] = useState("");
  const [badges, setBadges] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState("");
  const [showCrop, setShowCrop] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const itemImageRef = useRef<HTMLInputElement>(null);
  const [ingredientEntries, setIngredientEntries] = useState<IngredientEntry[]>([]);

  // Reset form when dialog opens with new item
  useEffect(() => {
    if (open) {
      setName(item?.name ?? "");
      setDescription(item?.description ?? "");
      setPrice(item ? String(item.price) : "");
      setOldPrice(item?.oldPrice ? String(item.oldPrice) : "");
      setPrices(item?.prices ? Object.fromEntries(Object.entries(item.prices).map(([k, v]) => [k, String(v)])) : {});
      setLoyaltyPoints(item?.loyaltyPoints ? String(item.loyaltyPoints) : "");
      setStock(item?.stock != null ? String(item.stock) : "");
      setImage(item?.image ?? "");
      setBadges(item?.badges ?? []);
      setIngredientEntries(
        item?.ingredients?.map((ii) => ({
          ingredientId: ii.ingredientId,
          ingredient: ii.ingredient,
          grams: ii.grams,
        })) ?? []
      );
    }
  }, [open, item]);

  const getFormData = () => ({
    name,
    description: description || undefined,
    price: Number(price),
    oldPrice: oldPrice ? Number(oldPrice) : null,
    prices: Object.keys(prices).length ? Object.fromEntries(Object.entries(prices).filter(([,v]) => v).map(([k, v]) => [k, Number(v)])) : undefined,
    loyaltyPoints: loyaltyPoints ? Number(loyaltyPoints) : undefined,
    stock: stock === "" ? null : Number(stock),
    image: image || undefined,
    badges,
    nameTranslations: Object.keys(nameTranslations).length ? nameTranslations : undefined,
    descTranslations: Object.keys(descTranslations).length ? descTranslations : undefined,
    ingredients: ingredientEntries.map((e) => ({
      ingredientId: e.ingredientId,
      grams: e.grams,
    })),
  } as Parameters<typeof createItem>[1]);

  const createMutation = useMutation({
    mutationFn: () => createItem(categoryId, getFormData()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items", categoryId] });
      onClose();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () => updateItem(item!.id || (item as unknown as Record<string, string>)._id, getFormData()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["items", categoryId] });
      onClose();
    },
  });

  const handleImageUpload = async (file: File) => {
    setUploading(true);
    try {
      const url = await uploadImage(file, "item", item?.id);
      setImage(url);
    } finally {
      setUploading(false);
    }
  };

  const [tab, setTab] = useState<"general" | "ingredients" | "translations">("general");
  const [nameTranslations, setNameTranslations] = useState<Record<string, string>>({});
  const [descTranslations, setDescTranslations] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open) {
      setTab("general");
      setNameTranslations(item?.nameTranslations ?? {});
      setDescTranslations(item?.descTranslations ?? {});
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (item) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const ALL_LANGS = [
    { code: "tr", label: "Türkçe", flag: "🇹🇷" },
    { code: "en", label: "English", flag: "🇬🇧" },
    { code: "de", label: "Deutsch", flag: "🇩🇪" },
    { code: "fr", label: "Français", flag: "🇫🇷" },
    { code: "es", label: "Español", flag: "🇪🇸" },
    { code: "it", label: "Italiano", flag: "🇮🇹" },
    { code: "pt", label: "Português", flag: "🇧🇷" },
    { code: "nl", label: "Nederlands", flag: "🇳🇱" },
    { code: "ru", label: "Русский", flag: "🇷🇺" },
    { code: "ar", label: "العربية", flag: "🇸🇦" },
    { code: "zh", label: "中文", flag: "🇨🇳" },
    { code: "ja", label: "日本語", flag: "🇯🇵" },
    { code: "ko", label: "한국어", flag: "🇰🇷" },
    { code: "hi", label: "हिन्दी", flag: "🇮🇳" },
    { code: "th", label: "ไทย", flag: "🇹🇭" },
    { code: "ms", label: "Bahasa Melayu", flag: "🇲🇾" },
    { code: "id", label: "Bahasa Indonesia", flag: "🇮🇩" },
    { code: "vi", label: "Tiếng Việt", flag: "🇻🇳" },
    { code: "pl", label: "Polski", flag: "🇵🇱" },
    { code: "uk", label: "Українська", flag: "🇺🇦" },
    { code: "el", label: "Ελληνικά", flag: "🇬🇷" },
    { code: "sv", label: "Svenska", flag: "🇸🇪" },
    { code: "da", label: "Dansk", flag: "🇩🇰" },
    { code: "fi", label: "Suomi", flag: "🇫🇮" },
    { code: "no", label: "Norsk", flag: "🇳🇴" },
    { code: "cs", label: "Čeština", flag: "🇨🇿" },
    { code: "ro", label: "Română", flag: "🇷🇴" },
    { code: "hu", label: "Magyar", flag: "🇭🇺" },
    { code: "he", label: "עברית", flag: "🇮🇱" },
    { code: "fa", label: "فارسی", flag: "🇮🇷" },
  ];

  const [activeLangs, setActiveLangs] = useState<string[]>([]);
  const [langPickerOpen, setLangPickerOpen] = useState(false);
  const [aiTranslating, setAiTranslating] = useState(false);
  const [aiImageOpen, setAiImageOpen] = useState(false);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [aiImagePreview, setAiImagePreview] = useState("");
  // Generate/reimagine sırasında oluşan hatayı dialog içinde göstermek için.
  // Eskiden catch block'ta yalnız `isPlanError` yakalanıyordu; diğer her şey
  // (OpenAI timeout, 402 yetersiz kredi, 429 rate-limit, 500) sessizce
  // yutulup loading kapanıyordu → kullanıcı "loading döndü ama bir şey
  // olmadı" yaşıyordu.
  const [aiImageError, setAiImageError] = useState("");
  const [aiSelectedStyle, setAiSelectedStyle] = useState("default");
  const [aiMode, setAiMode] = useState<"create" | "reimagine">("create");
  const [aiImageValid, setAiImageValid] = useState(false);
  const { data: bgStyles } = useQuery({ queryKey: ["ai-bg-styles"], queryFn: getAiBgStyles });
  const { data: creditData, refetch: refetchCredits } = useQuery({ queryKey: ["ai-credits", venueId], queryFn: () => getAiCredits(venueId), enabled: !!venueId });
  const [aiPickerOpen, setAiPickerOpen] = useState(false);
  const [aiTargetLangs, setAiTargetLangs] = useState<string[]>([]);

  useEffect(() => {
    if (open) {
      const existing = Object.keys({ ...item?.nameTranslations, ...item?.descTranslations });
      setActiveLangs(existing.length > 0 ? existing : []);
    }
  }, [open, item]);

  return (
    <>
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] sm:max-w-2xl flex flex-col">
        <DialogHeader>
          <DialogTitle>{item ? t("edit_item") : t("new_item")}</DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-1 border-b">
          {[
            { id: "general" as const, label: t("general") },
            { id: "ingredients" as const, label: t("ingredients") },
            { id: "translations" as const, label: t("translations") },
          ].map((tabItem) => (
            <button
              key={tabItem.id}
              type="button"
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === tabItem.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab(tabItem.id)}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto space-y-4 py-2">
          {/* General tab */}
          {tab === "general" && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 sm:col-span-1">
                  <Label>{t("item_name")} <span className="text-destructive">*</span></Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required />
                  <p className="mt-1 text-xs text-muted-foreground">{t("item_name_hint")}</p>
                </div>
                <div>
                  <Label>{t("price")} ({currency}) <span className="text-destructive">*</span></Label>
                  <Input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(e.target.value)} required />
                </div>
                <div>
                  <Label>{t("old_price")} ({currency})</Label>
                  <Input type="number" step="0.01" min="0" value={oldPrice} onChange={(e) => setOldPrice(e.target.value)} placeholder={t("optional")} />
                </div>
              </div>
              {/* Extra currencies */}
              {Object.keys(prices).length > 0 && (
                <div className="rounded-lg border p-3 space-y-2">
                  <Label className="text-xs text-muted-foreground">{t("price")} - {t("translations")}</Label>
                  {Object.entries(prices).map(([cur, val]) => (
                    <div key={cur} className="flex items-center gap-2">
                      <span className="w-12 text-sm font-medium">{cur}</span>
                      <Input type="number" step="0.01" min="0" className="h-8 flex-1" value={val} onChange={(e) => setPrices({ ...prices, [cur]: e.target.value })} />
                      <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => { const p = { ...prices }; delete p[cur]; setPrices(p); }}>
                        <X className="size-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                {["USD", "EUR", "GBP"].filter(c => !prices[c]).map(cur => (
                  <Button key={cur} type="button" variant="outline" size="sm" onClick={() => setPrices({ ...prices, [cur]: "" })}>
                    + {cur}
                  </Button>
                ))}
              </div>
              <div>
                <Label>Bonus Puan (opsiyonel)</Label>
                <Input type="number" min="0" step="1" value={loyaltyPoints} onChange={(e) => setLoyaltyPoints(e.target.value)} placeholder="Sipariş puanına ek bonus" />
                <p className="mt-1 text-xs text-muted-foreground">Bu ürün sipariş edildiğinde ekstra kazanılacak puan</p>
              </div>
              <div>
                <Label>{t("stock")}</Label>
                <Input type="number" min="0" step="1" value={stock} onChange={(e) => setStock(e.target.value)} placeholder={t("stock_placeholder")} />
                <p className="mt-1 text-xs text-muted-foreground">{t("stock_hint")}</p>
              </div>
              <div>
                <Label>{t("image")}</Label>
                <div className="flex items-center gap-3">
                  {image && <img src={image} alt="" className="size-16 rounded-lg object-cover" />}
                  <input
                    ref={itemImageRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) {
                        const reader = new FileReader();
                        reader.onload = () => { setCropSrc(reader.result as string); setShowCrop(true); };
                        reader.readAsDataURL(f);
                        e.target.value = "";
                      }
                    }}
                  />
                  <Button type="button" variant="outline" size="sm" onClick={() => itemImageRef.current?.click()} disabled={uploading || aiImageLoading}>
                    <Upload className="mr-2 size-4" />
                    {uploading ? t("uploading") : t("upload_image")}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={!name}
                    onClick={async () => {
                      setAiImageOpen(true);
                      setAiImagePreview("");
                      if (image) {
                        try {
                          const img = new Image();
                          img.src = image;
                          await new Promise<void>((resolve, reject) => { img.onload = () => resolve(); img.onerror = () => reject(); });
                          setAiImageValid(true);
                          setAiMode("reimagine");
                        } catch {
                          setAiImageValid(false);
                          setAiMode("create");
                        }
                      } else {
                        setAiImageValid(false);
                        setAiMode("create");
                      }
                    }}
                  >
                    <span className="flex items-center gap-1.5"><Sparkles className="size-4" />AI</span>
                  </Button>
                  {/* AI Image Generator Popup */}
                  {aiImageOpen && (
                    <>
                      <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => !aiImageLoading && setAiImageOpen(false)} />
                      <div className="fixed left-1/2 top-1/2 z-[101] w-96 -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card shadow-2xl overflow-hidden">
                        <div className="border-b px-4 py-3 flex items-center justify-between">
                          <p className="font-semibold text-sm flex items-center gap-2"><Sparkles className="size-4 text-primary" /> {t("ai_image_title")}</p>
                          {creditData && (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-md ${(creditData.credits ?? 0) < 10 ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                              {creditData.credits} {t("ai_credits")}
                            </span>
                          )}
                        </div>

                        {/* Mode tabs */}
                        <div className="flex gap-1 px-4 pt-3">
                          <button
                            type="button"
                            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${aiMode === "create" ? "bg-primary text-white" : "bg-muted text-muted-foreground"}`}
                            onClick={() => !aiImageLoading && setAiMode("create")}
                          >
                            {t("ai_image_create_mode")}
                          </button>
                          <button
                            type="button"
                            className={`flex-1 py-1.5 rounded-lg text-xs font-medium transition-colors ${aiMode === "reimagine" ? "bg-primary text-white" : "bg-muted text-muted-foreground"} ${!aiImageValid ? "opacity-40 cursor-not-allowed" : ""}`}
                            onClick={() => !aiImageLoading && aiImageValid && setAiMode("reimagine")}
                          >
                            {t("ai_image_reimagine_mode")}
                          </button>
                        </div>

                        {/* Preview area */}
                        <div className="p-4">
                          {aiImageLoading ? (
                            <div className="flex flex-col items-center justify-center h-48 rounded-lg bg-muted/50 border border-dashed">
                              <svg className="size-8 animate-spin text-primary mb-3" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                              <p className="text-sm font-medium">{t("ai_image_generating")}</p>
                              <p className="text-xs text-muted-foreground mt-1">{name}</p>
                            </div>
                          ) : aiImagePreview ? (
                            <img
                              src={aiImagePreview}
                              alt=""
                              className="w-full h-48 object-cover rounded-lg"
                              // S3/CDN cache veya partial response → görsel yüklenemezse
                              // sessizce kalmasın, kullanıcıya net mesaj.
                              onError={() => setAiImageError("Görsel oluştu fakat önizleme yüklenemedi. 'Tekrar Dene' veya 'Kullan'a basabilirsin.")}
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center h-48 rounded-lg bg-muted/50 border border-dashed">
                              <Sparkles className="size-8 text-muted-foreground mb-2" />
                              <p className="text-sm text-muted-foreground">{t("ai_image_select_style")}</p>
                            </div>
                          )}
                          {aiImageError && (
                            <p className="mt-2 text-xs text-destructive break-words">{aiImageError}</p>
                          )}
                        </div>

                        {/* Style selector */}
                        {!aiImageLoading && (
                          <div className="px-4 pb-2">
                            <p className="text-xs font-medium text-muted-foreground mb-2">{t("ai_image_style")}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {bgStyles?.map((style) => (
                                <button
                                  key={style.id}
                                  type="button"
                                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${aiSelectedStyle === style.id ? "bg-primary text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}
                                  onClick={() => setAiSelectedStyle(style.id)}
                                >
                                  {style.label}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="border-t p-3 flex gap-2">
                          {aiImagePreview && !aiImageLoading ? (
                            <>
                              <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={async () => {
                                  setAiImageLoading(true);
                                  setAiImageError("");
                                  try {
                                    const itemId = item?.id || (item as unknown as Record<string, string>)?._id;
                                    let result;
                                    if (aiMode === "reimagine" && image) {
                                      result = await aiReimageImage(venueId, { imageUrl: image, bgStyle: aiSelectedStyle, itemId });
                                    } else {
                                      const firstLang = activeLangs.find(l => nameTranslations[l]?.trim());
                                      result = await aiGenerateImage(venueId, { name: firstLang ? nameTranslations[firstLang] : name, description: firstLang ? (descTranslations[firstLang] || "") : "", itemId, bgStyle: aiSelectedStyle });
                                    }
                                    // Cache-bust: aynı itemId.jpg overwrite olduğunda browser/CDN
                                    // eski versiyonu önbellekten servisliyor → src aynı kalıyor,
                                    // <img> yeni istek atmıyor. ?v=timestamp ekleyince taze fetch.
                                    setAiImagePreview(`${result.url}?v=${Date.now()}`);
                                    refetchCredits();
                                  } catch (err) {
                                    if (isPlanError(err)) {
                                      setAiImageOpen(false);
                                      setUpgradeOpen(true);
                                    } else {
                                      // 402 / 429 / 500 / OpenAI timeout — kullanıcı ne olduğunu
                                      // bilmeli; eskiden sessizce yutuluyordu.
                                      const ax = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
                                      const msg = ax.response?.data?.error || ax.response?.data?.message || ax.message || "İşlem başarısız oldu";
                                      setAiImageError(typeof msg === "string" ? msg : "İşlem başarısız oldu");
                                    }
                                  } finally { setAiImageLoading(false); }
                                }}
                              >
                                {t("ai_image_retry")} ({aiMode === "reimagine" ? (creditData?.costs?.reimagine ?? 15) : (creditData?.costs?.image ?? 10)} {t("ai_credits")})
                              </Button>
                              <Button type="button" className="flex-1" onClick={() => { setImage(aiImagePreview); setAiImageOpen(false); }}>
                                {t("ai_image_use")}
                              </Button>
                            </>
                          ) : !aiImageLoading ? (
                            <>
                              <Button
                                type="button"
                                className="flex-1 gap-1.5"
                                disabled={aiMode === "reimagine" && !aiImageValid}
                                onClick={async () => {
                                  setAiImageLoading(true);
                                  setAiImageError("");
                                  try {
                                    const itemId = item?.id || (item as unknown as Record<string, string>)?._id;
                                    let result;
                                    if (aiMode === "reimagine" && image) {
                                      result = await aiReimageImage(venueId, { imageUrl: image, bgStyle: aiSelectedStyle, itemId });
                                    } else {
                                      const firstLang = activeLangs.find(l => nameTranslations[l]?.trim());
                                      result = await aiGenerateImage(venueId, { name: firstLang ? nameTranslations[firstLang] : name, description: firstLang ? (descTranslations[firstLang] || "") : "", itemId, bgStyle: aiSelectedStyle });
                                    }
                                    setAiImagePreview(`${result.url}?v=${Date.now()}`);
                                    refetchCredits();
                                  } catch (err) {
                                    if (isPlanError(err)) {
                                      setAiImageOpen(false);
                                      setUpgradeOpen(true);
                                    } else {
                                      const ax = err as { response?: { data?: { error?: string; message?: string } }; message?: string };
                                      const msg = ax.response?.data?.error || ax.response?.data?.message || ax.message || "İşlem başarısız oldu";
                                      setAiImageError(typeof msg === "string" ? msg : "İşlem başarısız oldu");
                                    }
                                  } finally { setAiImageLoading(false); }
                                }}
                              >
                                <Sparkles className="size-3.5" />
                                {t("ai_image_generate")} ({aiMode === "reimagine" ? (creditData?.costs?.reimagine ?? 15) : (creditData?.costs?.image ?? 10)} {t("ai_credits")})
                              </Button>
                              <Button type="button" variant="outline" onClick={() => setAiImageOpen(false)}>{t("cancel")}</Button>
                            </>
                          ) : (
                            <Button type="button" variant="outline" className="w-full" disabled>{t("ai_image_generating")}</Button>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <ImageCropDialog
                  open={showCrop}
                  onClose={() => { setShowCrop(false); setCropSrc(""); }}
                  imageSrc={cropSrc}
                  aspectRatio={1}
                  onCropped={(file) => handleImageUpload(file)}
                />
              </div>
              <div>
                <Label>{t("tags")}</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {BADGE_OPTIONS.map((b) => (
                    <Badge
                      key={b.id}
                      variant={badges.includes(b.id) ? "default" : "outline"}
                      className="cursor-pointer"
                      onClick={() => setBadges(badges.includes(b.id) ? badges.filter((x) => x !== b.id) : [...badges, b.id])}
                    >
                      {t(b.labelKey as Parameters<typeof t>[0])}
                    </Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Ingredients tab */}
          {tab === "ingredients" && (
            <div className="space-y-4">
              <div>
                <Label className="flex items-center gap-2 mb-2">
                  <Flame className="size-4 text-orange-500" />
                  {t("ingredients_materials")}
                </Label>
                <p className="text-xs text-muted-foreground mb-3">
                  {t("ingredients_desc")}
                </p>
              </div>
              <IngredientPicker entries={ingredientEntries} onChange={setIngredientEntries} />
              <NutritionSummary entries={ingredientEntries} />
            </div>
          )}

          {/* Translations tab */}
          {tab === "translations" && (
            <div className="space-y-4">
              <p className="text-xs text-muted-foreground">
                {t("translations_desc")}
              </p>

              {activeLangs.length > 0 && (
                <div className="rounded-lg border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="w-12 px-3 py-2 text-left"></th>
                        <th className="px-3 py-2 text-left font-medium">{t("item_name")}</th>
                        <th className="px-3 py-2 text-left font-medium">{t("description")}</th>
                        <th className="w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeLangs.map((code) => {
                        const lang = ALL_LANGS.find(l => l.code === code);
                        return (
                          <tr key={code} className="border-b last:border-0">
                            <td className="px-3 py-2 text-center text-lg" title={lang?.label}>{lang?.flag || "🌐"}</td>
                            <td className="px-2 py-2">
                              <Input className="h-8 text-sm" placeholder={name} value={nameTranslations[code] || ""} onChange={(e) => setNameTranslations({ ...nameTranslations, [code]: e.target.value })} />
                            </td>
                            <td className="px-2 py-2">
                              <Input className="h-8 text-sm" placeholder={description} value={descTranslations[code] || ""} onChange={(e) => setDescTranslations({ ...descTranslations, [code]: e.target.value })} />
                            </td>
                            <td className="px-2 py-2">
                              <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => {
                                setActiveLangs(activeLangs.filter(l => l !== code));
                                const nt = { ...nameTranslations }; delete nt[code]; setNameTranslations(nt);
                                const dt = { ...descTranslations }; delete dt[code]; setDescTranslations(dt);
                              }}>
                                <X className="size-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Add language & AI translate buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setLangPickerOpen(true)}
                >
                  <Plus className="mr-1 size-4" />
                  {t("add")} {t("language")}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={aiTranslating}
                  onClick={() => {
                    const src = activeLangs.find(l => nameTranslations[l]?.trim());
                    if (!src) { alert(t("translation_required")); return; }
                    const available = ALL_LANGS.filter(l => l.code !== src);
                    setAiTargetLangs(available.filter(l => activeLangs.includes(l.code) && !nameTranslations[l.code]?.trim()).map(l => l.code));
                    setAiPickerOpen(true);
                  }}
                >
                  {aiTranslating ? (
                    <><svg className="mr-1 size-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>{t("loading")}</>
                  ) : (
                    <><Sparkles className="mr-1 size-4" />AI {t("translations")}</>
                  )}
                </Button>

                {/* AI Translation Popup */}
                {aiPickerOpen && (
                  <>
                    <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setAiPickerOpen(false)} />
                    <div className="fixed left-1/2 top-1/2 z-[101] w-80 -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card shadow-2xl">
                      <div className="border-b px-4 py-3">
                        <p className="font-semibold text-sm flex items-center gap-2"><Sparkles className="size-4" /> AI {t("translations")}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(() => {
                            const src = activeLangs.find(l => nameTranslations[l]?.trim());
                            const srcLang = ALL_LANGS.find(l => l.code === src);
                            return `${srcLang?.flag || ""} ${srcLang?.label || ""} → ...`;
                          })()}
                        </p>
                      </div>
                      <div className="max-h-56 overflow-y-auto p-2">
                        {ALL_LANGS.filter(l => {
                          const src = activeLangs.find(al => nameTranslations[al]?.trim());
                          return l.code !== src;
                        }).map(l => (
                          <label key={l.code} className="flex items-center gap-3 px-3 py-2 text-sm hover:bg-muted rounded-lg cursor-pointer">
                            <input
                              type="checkbox"
                              checked={aiTargetLangs.includes(l.code)}
                              onChange={(e) => {
                                if (e.target.checked) setAiTargetLangs([...aiTargetLangs, l.code]);
                                else setAiTargetLangs(aiTargetLangs.filter(c => c !== l.code));
                              }}
                              className="rounded"
                            />
                            <span className="text-lg">{l.flag}</span>
                            <span>{l.label}</span>
                          </label>
                        ))}
                      </div>
                      <div className="border-t p-3 flex justify-end gap-2">
                        <Button type="button" variant="outline" size="sm" onClick={() => setAiPickerOpen(false)}>{t("cancel")}</Button>
                        <Button
                          type="button"
                          size="sm"
                          disabled={aiTargetLangs.length === 0 || aiTranslating}
                          onClick={async () => {
                            const src = activeLangs.find(l => nameTranslations[l]?.trim())!;
                            setAiPickerOpen(false);
                            setAiTranslating(true);
                            try {
                              const result = await aiTranslate(venueId, {
                                name: nameTranslations[src],
                                description: descTranslations[src] || "",
                                sourceLang: src,
                                targetLangs: aiTargetLangs,
                              });
                              // Add new langs to activeLangs if not already there
                              const newLangs = aiTargetLangs.filter(l => !activeLangs.includes(l));
                              if (newLangs.length > 0) setActiveLangs([...activeLangs, ...newLangs]);
                              setNameTranslations({ ...nameTranslations, ...result.nameTranslations });
                              setDescTranslations({ ...descTranslations, ...result.descTranslations });
                            } catch (err) {
                              if (isPlanError(err)) { setUpgradeOpen(true); } else { alert((err as Error).message || "AI translation failed"); }
                            } finally {
                              setAiTranslating(false);
                            }
                          }}
                        >
                          <Sparkles className="mr-1 size-4" />
                          {t("translations")} ({aiTargetLangs.length})
                        </Button>
                      </div>
                    </div>
                  </>
                )}
                {langPickerOpen && (
                  <>
                    <div className="fixed inset-0 z-[100] bg-black/50" onClick={() => setLangPickerOpen(false)} />
                    <div className="fixed left-1/2 top-1/2 z-[101] w-72 -translate-x-1/2 -translate-y-1/2 rounded-xl border bg-card shadow-2xl">
                      <div className="border-b px-4 py-3 font-semibold text-sm">{t("add")} {t("language")}</div>
                      <div className="max-h-64 overflow-y-auto">
                        {ALL_LANGS.filter(l => !activeLangs.includes(l.code)).map(l => (
                          <button
                            key={l.code}
                            type="button"
                            className="flex w-full items-center gap-3 px-4 py-3 text-sm hover:bg-muted transition-colors"
                            onClick={() => {
                              setActiveLangs([...activeLangs, l.code]);
                              setLangPickerOpen(false);
                            }}
                          >
                            <span className="text-lg">{l.flag}</span>
                            <span>{l.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeLangs.length === 0 && (
            <p className="text-sm text-destructive text-center">{t("translation_required")}</p>
          )}
          <Button type="submit" className="w-full" disabled={createMutation.isPending || updateMutation.isPending}>
            {item ? t("update") : t("add")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
    <UpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </>
  );
}

// === MenuDetailPage ===
function BulkPriceDialog({ open, onClose, venueId }: { open: boolean; onClose: () => void; venueId: string }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [percentage, setPercentage] = useState("");
  const [rounding, setRounding] = useState("round");

  const mutation = useMutation({
    mutationFn: () => bulkUpdatePrices(venueId, Number(percentage), rounding),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["items"] });
      alert(t("bulk_price_success").replace("{{count}}", String(data.updated)));
      onClose();
      setPercentage("");
    },
  });

  const pct = Number(percentage);
  const isValid = percentage !== "" && !isNaN(pct) && pct >= -99 && pct <= 1000 && pct !== 0;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="size-5" />
            {t("bulk_price")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("bulk_price_desc")}</p>
          <div>
            <Label>{t("percentage")}</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="1"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                placeholder="10"
              />
              <span className="text-lg font-bold text-muted-foreground">%</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {t("bulk_price_hint")}
            </p>
          </div>
          {isValid && (
            <div className={`rounded-lg border p-3 text-sm ${pct > 0 ? "bg-red-50 text-red-700 border-red-200" : "bg-green-50 text-green-700 border-green-200"}`}>
              {pct > 0
                ? t("bulk_price_increase").replace("{{pct}}", String(pct))
                : t("bulk_price_decrease").replace("{{pct}}", String(Math.abs(pct)))}
              <div className="mt-1 text-xs opacity-75">
                {t("bulk_price_example")
                  .replace("{{old}}", "100")
                  .replace("{{new}}", String(Math.max(0, Math.round(100 * (1 + pct / 100)))))}
              </div>
            </div>
          )}
          <div>
            <Label>{t("rounding")}</Label>
            <div className="flex gap-2 mt-1">
              {[
                { id: "round", label: t("round") },
                { id: "ceil", label: t("round_up") },
                { id: "floor", label: t("round_down") },
                { id: "none", label: t("no_rounding") },
              ].map((opt) => (
                <Badge
                  key={opt.id}
                  variant={rounding === opt.id ? "default" : "outline"}
                  className="cursor-pointer"
                  onClick={() => setRounding(opt.id)}
                >
                  {opt.label}
                </Badge>
              ))}
            </div>
          </div>
          <Button
            className="w-full"
            disabled={!isValid || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? t("updating") : t("update_prices")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MenuDetailPage() {
  const { venueId, menuId } = useParams<{ venueId: string; menuId: string }>();
  const { t } = useI18n();
  const [addCatOpen, setAddCatOpen] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [bulkPriceOpen, setBulkPriceOpen] = useState(false);
  const [menuQrOpen, setMenuQrOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);

  const { data: categories, isLoading } = useQuery({
    queryKey: ["categories", menuId],
    queryFn: () => getCategories(menuId!),
    enabled: !!menuId,
  });

  const { data: menus } = useQuery({
    queryKey: ["menus", venueId],
    queryFn: () => getMenus(venueId!),
    enabled: !!venueId,
  });
  const currentMenu = menus?.find((m) => m.id === menuId);

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground">{t("loading")}</div>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-2xl font-bold">{currentMenu?.name || t("menu_content")}</h1>
          {currentMenu && (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setRenameOpen(true)}
              title={t("rename_menu")}
            >
              <Edit2 className="size-4" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setBulkPriceOpen(true)}>
            <Percent className="mr-1 size-4" />
            {t("bulk_price")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setMenuQrOpen(true)}>
            <QrCode className="mr-1 size-4" />
            {t("menu_qr")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setPdfOpen(true)}>
            <FileDown className="mr-1 size-4" />
            PDF
          </Button>
          <Button onClick={() => setAddCatOpen(true)}>
            <Plus className="mr-2 size-4" />
            {t("add_category")}
          </Button>
        </div>
        <CategoryFormDialog
          open={addCatOpen}
          onClose={() => setAddCatOpen(false)}
          menuId={menuId!}
        />
        <PdfExportDialog
          open={pdfOpen}
          onClose={() => setPdfOpen(false)}
          venueId={venueId!}
          menuId={menuId!}
          primaryColor="#059669"
        />
        <BulkPriceDialog
          open={bulkPriceOpen}
          onClose={() => setBulkPriceOpen(false)}
          venueId={venueId!}
        />
        <MenuQrDialog
          open={menuQrOpen}
          onClose={() => setMenuQrOpen(false)}
          venueId={venueId!}
          menuId={menuId!}
        />
        <MenuRenameDialog
          open={renameOpen}
          onClose={() => setRenameOpen(false)}
          venueId={venueId!}
          menuId={menuId!}
          currentName={currentMenu?.name || ""}
        />
      </div>

      {categories?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Package className="mb-4 size-12 text-muted-foreground" />
          <p className="text-muted-foreground">{t("no_categories")}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories?.map((cat) => (
            <CategorySection
              key={cat.id}
              category={cat}
              venueId={venueId!}
              menuId={menuId!}
              currency="TRY"
            />
          ))}
        </div>
      )}
    </div>
  );
}
